import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Product } from '../../../core/models/product.model';
import { ProductService } from '../../../core/services/product.service';

@Component({
  selector: 'app-product-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './product-form-modal.component.html',
  styleUrls: ['./product-form-modal.component.scss'],
})
export class ProductFormModalComponent implements OnChanges, OnDestroy {
  @Input() product: Product | null = null;
  @Input() parentProducts: Product[] = [];
  @Input() nextDisplayOrder = 1;
  @Output() saveProduct = new EventEmitter<Product>();
  @Output() cancel = new EventEmitter<void>();

  readonly productForm = this.formBuilder.nonNullable.group({
    productName: ['', Validators.required],
    productType: ['parent' as Product['type'], Validators.required],
    parentId: [''],
    category: ['', Validators.required],
    imageUrl: ['assets/sr-organic/mushroom-hero.png'],
    themeColor: ['#c8ef5a', [Validators.required, Validators.pattern(/^#[0-9a-fA-F]{6}$/)]],
    status: ['active' as Product['status'], Validators.required],
    featuredProduct: [false],
    displayOrder: [1, Validators.required],
    shortDescription: ['', Validators.required],
    longDescription: [''],
  });

  private readonly productTypeSubscription: Subscription;
  private readonly parentSubscription: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly productService: ProductService,
  ) {
    this.productTypeSubscription = this.productForm.controls.productType.valueChanges.subscribe((productType) => {
      this.updateParentValidation(productType);
    });

    this.parentSubscription = this.productForm.controls.parentId.valueChanges.subscribe((parentId) => {
      this.syncParentCategory(parentId);
    });
  }

  get activeParentProducts(): Product[] {
    return this.parentProducts
      .filter((parent) => parent.status === 'active' || parent.id === this.product?.parentId)
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
  }

  get isChildProduct(): boolean {
    return this.productForm.controls.productType.value === 'child';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['product'] && !changes['nextDisplayOrder']) {
      return;
    }

    const product = this.product;

    this.productForm.reset({
      productName: product?.name ?? '',
      productType: product?.type ?? 'parent',
      parentId: product?.parentId ?? '',
      category: product?.category ?? '',
      imageUrl: product?.imageUrl ?? 'assets/sr-organic/mushroom-hero.png',
      themeColor: product?.themeColor ?? '#c8ef5a',
      status: product?.status ?? 'active',
      featuredProduct: false,
      displayOrder: product?.displayOrder ?? this.nextDisplayOrder,
      shortDescription: product?.shortDescription ?? '',
      longDescription: product?.longDescription ?? '',
    });

    this.updateParentValidation(this.productForm.controls.productType.value);
  }

  ngOnDestroy(): void {
    this.productTypeSubscription.unsubscribe();
    this.parentSubscription.unsubscribe();
  }

  submit(): void {
    this.updateParentValidation(this.productForm.controls.productType.value);

    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    const value = this.productForm.getRawValue();
    const now = new Date().toISOString();
    const shortDescription = value.shortDescription.trim();

    this.saveProduct.emit({
      id: this.product?.id ?? this.productService.createProductId(),
      name: value.productName.trim(),
      slug: this.product?.slug ?? '',
      type: value.productType,
      parentId: value.productType === 'child' ? value.parentId : null,
      category: value.category.trim(),
      shortDescription,
      longDescription: value.longDescription.trim() || shortDescription,
      imageUrl: value.imageUrl.trim() || 'assets/sr-organic/mushroom-hero.png',
      themeColor: value.themeColor,
      status: value.status,
      displayOrder: Number(value.displayOrder),
      createdAt: this.product?.createdAt ?? this.product?.createdOn ?? now,
      updatedAt: now,
      createdOn: this.product?.createdAt ?? this.product?.createdOn ?? now,
      updatedOn: now,
    });
  }

  showError(controlName: keyof typeof this.productForm.controls): boolean {
    const control = this.productForm.controls[controlName];
    return control.invalid && control.touched;
  }

  private updateParentValidation(productType: Product['type']): void {
    const parentControl = this.productForm.controls.parentId;

    if (productType === 'child') {
      parentControl.addValidators(Validators.required);
    } else {
      parentControl.clearValidators();
      parentControl.setValue('', { emitEvent: false });
    }

    parentControl.updateValueAndValidity({ emitEvent: false });
  }

  private syncParentCategory(parentId: string): void {
    if (!parentId || !this.isChildProduct) {
      return;
    }

    const parent = this.parentProducts.find((product) => product.id === parentId);

    if (parent) {
      this.productForm.controls.category.setValue(parent.category);
      this.productForm.controls.themeColor.setValue(parent.themeColor ?? '#c8ef5a');
    }
  }
}
